import {
  CHECKOUT_SCHEDULE_MODE,
  CHECKOUT_SPLIT_MODE,
  SCHEDULE_KIND,
  SCHEDULE_UI,
  type CheckoutDeliveryPlanProps,
  type CheckoutSchedulePickerProps,
} from '../constants/schedule'
import { cx } from '../helpers/format'
import '../styles/checkout-page.scss'

export function CheckoutDeliveryPlan({
  estimate,
  canSplit,
  canSchedule,
  splitMode,
  onSplitModeChange,
  scheduleMode,
  onScheduleModeChange,
  dates,
  selectedDate,
  onDateChange,
  slots,
  selectedSlot,
  onSlotChange,
  canScheduleExpress,
  expressItems,
  standardItems,
  expressScheduleMode,
  onExpressScheduleModeChange,
  expressDates,
  expressSelectedDate,
  onExpressDateChange,
  expressSlots,
  expressSelectedSlot,
  onExpressSlotChange,
  disabled,
}: CheckoutDeliveryPlanProps) {
  const combined = splitMode === CHECKOUT_SPLIT_MODE.combined
  const showHeading = canSplit || (canScheduleExpress && canSchedule)
  const expressChoices = estimate.tone === 'express'

  return (
    <section
      className={cx('checkout-card', 'checkout-delivery', expressChoices && 'checkout-delivery--choices')}
    >
      {expressChoices ? (
        <header className="checkout-card__head">
          <h2>{SCHEDULE_UI.planHeading}</h2>
        </header>
      ) : (
        <header
          className={cx(
            'checkout-card__head',
            'checkout-estimate',
            `checkout-estimate--${estimate.tone}`,
          )}
        >
          <h2>{estimate.title}</h2>
          {estimate.lead ? <p>{estimate.lead}</p> : null}
        </header>
      )}

      {canSplit ? (
        <div className="checkout-plan">
          <label
            className={cx(
              'checkout-plan__option',
              splitMode === CHECKOUT_SPLIT_MODE.split && 'is-selected',
            )}
          >
            <input
              type="radio"
              name="checkoutSplitMode"
              checked={splitMode === CHECKOUT_SPLIT_MODE.split}
              onChange={() => onSplitModeChange(CHECKOUT_SPLIT_MODE.split)}
              disabled={disabled}
            />
            <span>
              <strong>{SCHEDULE_UI.splitTitle}</strong>
              <em>{SCHEDULE_UI.splitHint}</em>
            </span>
          </label>
          <label
            className={cx(
              'checkout-plan__option',
              splitMode === CHECKOUT_SPLIT_MODE.combined && 'is-selected',
            )}
          >
            <input
              type="radio"
              name="checkoutSplitMode"
              checked={splitMode === CHECKOUT_SPLIT_MODE.combined}
              onChange={() => onSplitModeChange(CHECKOUT_SPLIT_MODE.combined)}
              disabled={disabled}
            />
            <span>
              <strong>{SCHEDULE_UI.combinedTitle}</strong>
              <em>{SCHEDULE_UI.combinedHint}</em>
            </span>
          </label>
        </div>
      ) : null}

      {canScheduleExpress ? (
        <SchedulePicker
          kind={SCHEDULE_KIND.express}
          showHeading={showHeading}
          heading={SCHEDULE_UI.expressGroupHeading}
          lead={expressItems}
          asapTitle={SCHEDULE_UI.scheduleExpressAsapTitle}
          asapHint={SCHEDULE_UI.scheduleExpressAsapHint}
          pickTitle={SCHEDULE_UI.scheduleExpressPickTitle}
          pickHint={SCHEDULE_UI.scheduleExpressPickHint}
          emptyLabel={SCHEDULE_UI.expressNoSlots}
          mode={expressScheduleMode}
          onModeChange={onExpressScheduleModeChange}
          dates={expressDates}
          selectedDate={expressSelectedDate}
          onDateChange={onExpressDateChange}
          slots={expressSlots}
          selectedSlot={expressSelectedSlot}
          onSlotChange={onExpressSlotChange}
          plain={!showHeading}
          disabled={disabled}
        />
      ) : null}

      {canSchedule ? (
        <SchedulePicker
          kind={SCHEDULE_KIND.standard}
          showHeading={showHeading}
          heading={combined ? SCHEDULE_UI.scheduleHeading : SCHEDULE_UI.standardGroupHeading}
          lead={combined ? SCHEDULE_UI.scheduleLeadCombined : standardItems}
          asapTitle={SCHEDULE_UI.scheduleAsapTitle}
          asapHint={SCHEDULE_UI.scheduleAsapHint}
          pickTitle={SCHEDULE_UI.schedulePickTitle}
          pickHint={SCHEDULE_UI.schedulePickHint}
          emptyLabel={SCHEDULE_UI.noSlots}
          mode={scheduleMode}
          onModeChange={onScheduleModeChange}
          dates={dates}
          selectedDate={selectedDate}
          onDateChange={onDateChange}
          slots={slots}
          selectedSlot={selectedSlot}
          onSlotChange={onSlotChange}
          plain={!showHeading}
          disabled={disabled}
        />
      ) : null}
    </section>
  )
}

function SchedulePicker({
  kind,
  showHeading,
  heading,
  lead,
  asapTitle,
  asapHint,
  pickTitle,
  pickHint,
  emptyLabel,
  mode,
  onModeChange,
  dates,
  selectedDate,
  onDateChange,
  slots,
  selectedSlot,
  onSlotChange,
  plain,
  disabled,
}: CheckoutSchedulePickerProps) {
  const scheduled = mode === CHECKOUT_SCHEDULE_MODE.scheduled
  const pickDisabled = disabled || dates.length === 0
  const modeName = `checkoutScheduleMode-${kind}`

  return (
    <div className={plain ? 'checkout-schedule checkout-schedule--plain' : 'checkout-schedule'}>
      {showHeading ? (
        <header className="checkout-card__head checkout-schedule__head">
          <h2>{heading}</h2>
          {lead ? <p className="checkout-schedule__items">{lead}</p> : null}
        </header>
      ) : null}
      <div className="checkout-schedule__modes" role="radiogroup" aria-label={heading}>
        <label className="checkout-schedule__mode">
          <input
            type="radio"
            name={modeName}
            checked={!scheduled}
            onChange={() => onModeChange(CHECKOUT_SCHEDULE_MODE.asap)}
            disabled={disabled}
          />
          <span>
            <strong>{asapTitle}</strong>
            {asapHint ? <em>{asapHint}</em> : null}
          </span>
        </label>
        <label className="checkout-schedule__mode">
          <input
            type="radio"
            name={modeName}
            checked={scheduled}
            onChange={() => onModeChange(CHECKOUT_SCHEDULE_MODE.scheduled)}
            disabled={pickDisabled}
          />
          <span>
            <strong>{pickTitle}</strong>
            <em>{pickHint}</em>
          </span>
        </label>
      </div>
      {scheduled ? (
        <div className="checkout-slots">
          <p className="checkout-slots__label">{SCHEDULE_UI.dateLabel}</p>
          <div className="checkout-slots__dates" role="list">
            {dates.map((date) => (
              <button
                key={date.key}
                type="button"
                className={cx(
                  'checkout-slots__chip',
                  selectedDate === date.key && 'is-selected',
                )}
                onClick={() => onDateChange(date.key)}
                disabled={disabled}
              >
                {date.label}
              </button>
            ))}
          </div>
          <p className="checkout-slots__label">{SCHEDULE_UI.slotLabel}</p>
          {slots.length === 0 ? (
            <p className="checkout-slots__empty">{emptyLabel}</p>
          ) : (
            <div className="checkout-slots__times" role="list">
              {slots.map((slot) => (
                <button
                  key={slot.iso}
                  type="button"
                  className={cx(
                    'checkout-slots__chip',
                    selectedSlot === slot.iso && 'is-selected',
                  )}
                  onClick={() => onSlotChange(slot.iso)}
                  disabled={disabled}
                >
                  {slot.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
